import React, { useState, useContext } from 'react'
import { useLocation } from 'react-router-dom'

import WEEKDAYS from '../../constants/common'
import { PREVIEW_SCHEDULE } from '../../constants/previewValues'
import {
  EYE_ICON,
  HIDDEN_EYE_ICON,
  LOCATION_ICON,
  PERSON_ICON,
  STOREFRONT_ICON
} from '../../constants/urls'
import { DataContext } from '../../context/DataContext'
import { useApi } from '../../hooks/useApi'
import { capitalize } from '../../utils/string_util'

import Button from '../shared/Button'
import Card from '../shared/Card'
import Loader from '../shared/Loader'
import Modal from '../shared/Modal'

interface PersonalDataComponentProps {
  handleAuthCheck: () => boolean
}

const RAINDOOR_PERU_COMPANY_ID = '3493'

const PersonalDataComponent: React.FC<PersonalDataComponentProps> = (
  { handleAuthCheck }: PersonalDataComponentProps
) => {
  const { sale, companyColor, loggedIn } = useContext(DataContext)
  const { getWarehouseExpandedData } = useApi()
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const preview = query.get('preview')

  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null)

  const fetchSchedule = async () => {
    if (preview) {
      setWarehouse(PREVIEW_SCHEDULE)
      return
    }
    setIsLoading(true)
    const response = await getWarehouseExpandedData()
    if (response.error) {
      setIsOpen(false)
      handleAuthCheck()
      return
    }
    setWarehouse(response)
    setIsLoading(false)
  }

  const handleOpenSchedule = async () => {
    setIsOpen(true)
    if (!warehouse || sale.warehouseId !== warehouse.id) {
      await fetchSchedule()
    }
  }

  const normalizeFlappDestinationName = (destinationName: string | undefined): string | undefined => (
    destinationName?.replace(/[\u26A1]/g, '')?.trim()
  )

  const shouldUseInternationalDeliveryAddress = (
    String(sale.companyId) === RAINDOOR_PERU_COMPANY_ID &&
    Boolean(sale.internationalDeliveryAddress)
  )

  const deliveryAddress = (shouldUseInternationalDeliveryAddress && sale.internationalDeliveryAddress) ||
    sale.deliveryAddress
  const deliveryCity = shouldUseInternationalDeliveryAddress
    ? sale.internationalDeliveryCity
    : sale.city
  const deliveryStateName = shouldUseInternationalDeliveryAddress
    ? sale.internationalDeliveryStateName
    : sale.stateName

  const dayMapper = {
    'monday': 'Lunes',
    'tuesday': 'Martes',
    'wednesday': 'Miércoles',
    'thursday': 'Jueves',
    'friday': 'Viernes',
    'saturday': 'Sábado',
    'sunday': 'Domingo'
  }

  const getScheduleFormatted = (schedule: { startTime: string, finishTime: string }) => {
    const startTime = Number(schedule.startTime.split(':')[0])
    const finishTime = Number(schedule.finishTime.split(':')[0])
    let startTimeFormatted = ''
    let finishTimeFormatted = ''
    if (startTime < 12) {
      startTimeFormatted = `${startTime}:00 a.m.`
    } else {
      startTimeFormatted = `${startTime - 12}:00 p.m.`
    }
    if (finishTime < 12) {
      finishTimeFormatted = `${finishTime}:00 a.m.`
    } else {
      finishTimeFormatted = `${finishTime - 12}:00 p.m.`
    }

    return `${startTimeFormatted} - ${finishTimeFormatted}`
  }

  return (
    <div className='landscape:mt-4 portrait:mt-4'>
      <div className='flex justify-between'>
        <h1 className='text-base font-[Geist] ml-4'>Detalle</h1>
        {!loggedIn && (
          <>
            <button type='button' className='flex gap-2 portrait:hidden' onClick={handleAuthCheck}>
              <img className='w-5 h-5' src={`${EYE_ICON}`} alt='eye' />
              <p>Ver Información</p>
            </button>
            <button type='button' className='flex gap-2 landscape:hidden mr-2' onClick={handleAuthCheck}>
              <img className='w-5 h-5' src={`${HIDDEN_EYE_ICON}`} alt='eye' />
            </button>
          </>
        )}
        {
          loggedIn && (
            <div className='flex gap-2 landscape:hidden mr-2'>
              <img className='w-5 h-5' src={`${EYE_ICON}`} alt='eye' />
            </div>
          )
        }
      </div>

      <div className='items-stretch portrait:flex min-w-full landscape:grid landscape:grid-cols-2 gap-3 mt-1 portrait:overflow-x-auto portrait:scrollbar-hide md:portrait:overflow-visible'>
        <Card className='flex flex-col gap-4 landscape:overflow-x-auto p-4 landscape:scrollbar-hide w-auto portrait:min-w-60 portrait:flex-shrink-0 md:portrait:flex-1 landscape:pt-4 landscape:pb-6'>
          <div className='flex gap-2 ml-2'>
            <img className='w-3 h-3 mt-[5px]' src={`${PERSON_ICON}`} alt='person' />
            <h2 className='text-base font-[Onest] font-medium'>Mis Datos</h2>
          </div>

          <div className='flex flex-col gap-1 text-gray-500 font-[Geist]'>
            <p className='whitespace-nowrap'>{capitalize(sale.clientName)} {capitalize(sale.clientLastName)}</p>
            <p className='whitespace-nowrap'>{sale.clientPhone}</p>
            <p className='whitespace-nowrap'>{sale.clientEmail}</p>
          </div>
        </Card>

        {sale.deliveryType === 'homeDelivery' && (
          <Card className='flex flex-col gap-4 landscape:overflow-x-auto landscape:scrollbar-hide w-auto portrait:min-w-60 portrait:flex-shrink-0 md:portrait:flex-1 landscape:pt-4 landscape:pb-6'>
            <div className='flex gap-2 ml-2'>
              <img className='w-4 h-4 mt-[5px]' src={`${LOCATION_ICON}`} alt='delivery' />
              <h2 className='text-base font-[Onest] font-medium'>Despacho</h2>
            </div>

            <div className='flex flex-col gap-1 text-gray-500 font-[Geist]'>
              <p className='whitespace-nowrap'>{capitalize(deliveryAddress)}</p>
              {deliveryCity && <p className='whitespace-nowrap'>{deliveryCity}</p>}
              {deliveryStateName && <p className='whitespace-nowrap'>{deliveryStateName}</p>}
            </div>
          </Card>
        )}

        {(sale.deliveryType === 'clickAndCollect' || sale.deliveryType === 'storePickup') && (
          <Card className='flex flex-col gap-4 overflow-x-auto p-4 scrollbar-hide w-auto portrait:flex-shrink-0 portrait:min-w-60 md:flex-1 landscape:pt-4 landscape:pb-6'>
            <div className='flex gap-6 w-full justify-between'>
              <div className='flex gap-2 ml-2'>
                <img className='w-4 h-4 mt-[5px]' src={`${STOREFRONT_ICON}`} alt='delivery' />
                <h2 className='text-lg font-[Onest] font-medium'>Retiro</h2>
              </div>

              {sale.managedByPinflag && (
                <button type='button' onClick={handleOpenSchedule}>
                  <p className='underline whitespace-nowrap'>Ver Horario</p>
                </button>
              )}
            </div>

            <div className='flex flex-col gap-1 text-gray-500 font-[Geist]'>
              <p className='whitespace-nowrap'>{sale.warehouseName || normalizeFlappDestinationName(sale.flappData?.destinationName) || '*******'}</p>
              {sale.managedByPinflag && (
                <>
                  <p className='whitespace-nowrap'>{capitalize(deliveryAddress)}</p>
                  {deliveryCity && <p className='whitespace-nowrap'>{deliveryCity}</p>}
                  {deliveryStateName && <p className='whitespace-nowrap'>{deliveryStateName}</p>}
                </>
              )}
              {!sale.managedByPinflag && (
                <div className='mt-10'/>
              )}
            </div>
          </Card>
        )}
      </div>

      <Modal show={isOpen}>
        {isLoading ? <Loader /> : (
        <div className='flex flex-col gap-4 items-center'>
          <h1 className='text-xl font-bold'>Horario</h1>
          <h2 className='text-lg text-gray-500'>{warehouse?.name}</h2>

          <div className='bg-[#F7F7F7] p-4 rounded-xl w-full'>
            { WEEKDAYS.map((day: string) => {
              const daySchedule = warehouse?.schedules.find((schedule) => schedule.day === day)
              if (daySchedule) {
              return (
                <div className='grid grid-cols-2 gap-2 w-full' key={day}>
                <p className='text-sm text-gray-500'>{dayMapper[day as keyof typeof dayMapper]}</p>
                <p className='text-sm text-gray-500'>{getScheduleFormatted(daySchedule)}</p>
              </div>)
            }
            return null
          })}
          </div>

          <div className='w-full h-10'>
            <Button
              text='Listo'
              onClick={() => setIsOpen(false)}
              dark
              companyColor={companyColor}
            />
          </div>
        </div>
        )}
      </Modal>
    </div>
  )
}

export default PersonalDataComponent
